import type { TransportOption, TripStop } from 'tripPlanner/api/types'
import { getAirportsSearch } from 'api/flights'
import type { FlightSearchDrawerParams } from './flightSearchDrawerParams'
import { getFlightSearchDrawerParamsFromTransport } from './flightSearchDrawerParams'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { isFlightTransportOption } from '../../utils/transportOptionUtils'

export interface StopFlightSearchDefaults {
  flightSearchParams: FlightSearchDrawerParams
  existingFlights?: Array<TransportOption>
}

function haveSameStopLocation(firstStop: TripStop, lastStop: TripStop): boolean {
  const firstPlaceId = firstStop.stopPlaceDetail?.placeId
  const lastPlaceId = lastStop.stopPlaceDetail?.placeId
  if (firstPlaceId || lastPlaceId) return firstPlaceId !== undefined && firstPlaceId === lastPlaceId

  const firstPlaceName = firstStop.placeName?.trim().toLocaleLowerCase()
  const lastPlaceName = lastStop.placeName?.trim().toLocaleLowerCase()
  const firstPlaceCountry = firstStop.placeCountry?.trim().toLocaleLowerCase()
  const lastPlaceCountry = lastStop.placeCountry?.trim().toLocaleLowerCase()

  return firstPlaceName !== undefined &&
    firstPlaceCountry !== undefined &&
    firstPlaceName === lastPlaceName &&
    firstPlaceCountry === lastPlaceCountry
}

/** A return search is used for the outer loop or a stop whose adjacent stops match. */
export function getItineraryFlightSearchDefaults(stops: Array<TripStop>, stop: TripStop): FlightSearchDrawerParams {
  const itineraryStops = sortTripStopsByItineraryOrder(stops)
    .filter(candidate => candidate.stopType !== 'ORIGIN')
  const firstStop = itineraryStops[0]
  const lastStop = itineraryStops.at(-1)
  const stopIndex = itineraryStops.findIndex(candidate => candidate.id === stop.id)

  if (!firstStop || !lastStop || stopIndex === -1) return { fareType: 'oneWay' }

  const isOuterReturnJourney = stopIndex === 0 && (
    itineraryStops.length === 1 || haveSameStopLocation(firstStop, lastStop)
  )
  if (isOuterReturnJourney) {
    const returnDate = lastStop.endDate ?? lastStop.startDate
    return { fareType: 'return', returnDate }
  }

  const previousStop = itineraryStops[stopIndex - 1]
  const nextStop = itineraryStops[stopIndex + 1]
  if (previousStop && nextStop && haveSameStopLocation(previousStop, nextStop)) {
    const returnDate = stop.endDate ?? stop.startDate
    return { fareType: 'return', returnDate }
  }

  return { fareType: 'oneWay' }
}

function chooseAirport(airports: Array<App.AirportLocation>): App.AirportLocation | undefined {
  return airports.find(airport => airport.isMultiAirport === true && airport.airportCode.endsWith('ALL')) ??
    airports.find(airport => /^[A-Z]{3}$/.test(airport.airportCode)) ??
    airports[0]
}

function getPreviousTripStop(stops: Array<TripStop>, stop: TripStop): TripStop | undefined {
  const orderedStops = sortTripStopsByItineraryOrder(stops)
  const stopIndex = orderedStops.findIndex(candidate => candidate.id === stop.id)
  return stopIndex > 0 ? orderedStops[stopIndex - 1] : undefined
}

export function getAdjacentStopFlightSearchDefaults(stops: Array<TripStop>, stop: TripStop): StopFlightSearchDefaults | undefined {
  const previousStop = getPreviousTripStop(stops, stop)
  const transports = previousStop?.transports
  const transport = transports?.find(candidate =>
    candidate.arrivalStopId === stop.id && candidate.options.some(isFlightTransportOption),
  ) ?? transports?.find(candidate => candidate.options.some(isFlightTransportOption))
  const option = transport?.options.find(isFlightTransportOption)
  if (!transport || !option) return undefined

  return {
    flightSearchParams: getFlightSearchDrawerParamsFromTransport(option, transport),
    existingFlights: transport.options,
  }
}

/**
 * Applies itinerary intent only when the selected transport did not already carry
 * saved flight-search intent. All flight drawer entry points must use this merge.
 */
export function getFlightSearchDrawerParamsWithItineraryDefaults(
  stops: Array<TripStop>,
  stop: TripStop,
  params: FlightSearchDrawerParams,
): FlightSearchDrawerParams {
  const itineraryDefaults = getItineraryFlightSearchDefaults(stops, stop)

  return {
    ...itineraryDefaults,
    ...params,
    fareType: params.fareType ?? itineraryDefaults.fareType,
    returnDate: params.returnDate ?? itineraryDefaults.returnDate,
  }
}

export function getFlightSearchDrawerParamsForTransport(
  stops: Array<TripStop>,
  option: TransportOption,
  transport: Parameters<typeof getFlightSearchDrawerParamsFromTransport>[1],
  returnOption?: TransportOption,
): FlightSearchDrawerParams {
  const params = getFlightSearchDrawerParamsFromTransport(option, transport, returnOption)
  const arrivalStop = stops.find(stop => stop.id === transport?.arrivalStopId)

  return arrivalStop
    ? getFlightSearchDrawerParamsWithItineraryDefaults(stops, arrivalStop, params)
    : params
}

export function getStopFlightSearchDefaults(stops: Array<TripStop>, stop: TripStop): StopFlightSearchDefaults {
  const adjacentFlightDefaults = getAdjacentStopFlightSearchDefaults(stops, stop)
  if (adjacentFlightDefaults) {
    return {
      ...adjacentFlightDefaults,
      flightSearchParams: getFlightSearchDrawerParamsWithItineraryDefaults(
        stops,
        stop,
        adjacentFlightDefaults.flightSearchParams,
      ),
    }
  }

  const previousStop = getPreviousTripStop(stops, stop)

  return {
    flightSearchParams: getFlightSearchDrawerParamsWithItineraryDefaults(stops, stop, {
      originAirportName: previousStop?.placeName,
      destinationAirportName: stop.placeName,
      departureDate: previousStop ? stop.startDate : undefined,
    }),
  }
}

/** Resolves name-only stop defaults to valid airport records before opening the flight drawer. */
export async function resolveStopFlightSearchAirportDefaults(
  stops: Array<TripStop>,
  stop: TripStop,
  params: FlightSearchDrawerParams,
): Promise<FlightSearchDrawerParams> {
  if (params.useSavedAirports) return params
  const previousStop = getPreviousTripStop(stops, stop)
  const [originAirport, destinationAirport] = await Promise.all([
    params.originAirportCode || !previousStop?.placeName
      ? undefined
      : getAirportsSearch(previousStop.placeName).then(chooseAirport).catch(() => undefined),
    params.destinationAirportCode || !stop.placeName
      ? undefined
      : getAirportsSearch(stop.placeName).then(chooseAirport).catch(() => undefined),
  ])

  return {
    ...params,
    originAirportCode: params.originAirportCode ?? originAirport?.airportCode,
    originAirportName: params.originAirportName ?? originAirport?.cityAirportName ?? originAirport?.airportName,
    destinationAirportCode: params.destinationAirportCode ?? destinationAirport?.airportCode,
    destinationAirportName: params.destinationAirportName ?? destinationAirport?.cityAirportName ?? destinationAirport?.airportName,
  }
}
